/**
 * v77.99.77: Small presentational primitives used across the admin
 * dashboard. Extracted from pages/AdminDashboard.jsx so they can be
 * reused by future admin pages (insights deep-dive, abuse console,
 * etc.) without copy-pasting the calm-palette styling.
 *
 * All three components are stateless and prop-driven — they accept a
 * `testid` so the parent can drive deterministic Playwright selectors.
 */
import { useMemo } from "react";

/** Small stat tile — matches the calm, neutral palette used on Dashboard.jsx. */
export const Tile = ({ icon: Icon, label, value, accent = "teal", testid }) => {
  const palette = {
    teal:    { bar: "#1F9FA4", iconBg: "bg-[#E0F2F2]", iconFg: "text-[#116B6E]" },
    sand:    { bar: "#B88A3A", iconBg: "bg-[#FBF0DC]", iconFg: "text-[#6F4E1E]" },
    violet:  { bar: "#7A5AA6", iconBg: "bg-[#ECE3F3]", iconFg: "text-[#4E3877]" },
    neutral: { bar: "#6F828A", iconBg: "bg-[#EEF3F4]", iconFg: "text-[#4A6170]" },
    rose:    { bar: "#C0291D", iconBg: "bg-[#FBEFED]", iconFg: "text-[#8C2A22]" },
  }[accent] || { bar: "#1F9FA4", iconBg: "bg-[#E0F2F2]", iconFg: "text-[#116B6E]" };
  return (
    <div
      className="relative overflow-hidden border border-[#D9E1E3] bg-white rounded-md px-5 pt-[18px] pb-5"
      style={{ borderTop: `3px solid ${palette.bar}` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium pt-0.5">{label}</div>
        <div className={`w-8 h-8 rounded-sm ${palette.iconBg} ${palette.iconFg} inline-flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-4 font-heading text-3xl font-semibold tabular-nums text-[#2E3A3F]" data-testid={`${testid}-value`}>
        {value === undefined || value === null ? "—" : Number(value).toLocaleString()}
      </div>
    </div>
  );
};

/** Dependency-free horizontal bar — good enough for a privacy-first admin view. */
export const BarRow = ({ label, count, max, testid }) => {
  const pct = max > 0 ? Math.max(2, Math.round((count / max) * 100)) : 2;
  return (
    <div className="text-sm" data-testid={testid}>
      <div className="flex justify-between items-baseline">
        <span className="text-[#2E3A3F]">{label}</span>
        <span className="font-mono-data tabular-nums text-[#6F828A]">{count.toLocaleString()}</span>
      </div>
      <div className="mt-1 h-2 rounded-sm bg-[#EEF3F4] overflow-hidden">
        <div className="h-full bg-[#1F9FA4]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/** Tiny sparkline/bar chart for signups. No external lib. */
export const SignupsChart = ({ series }) => {
  const max = useMemo(() => Math.max(1, ...series.map((p) => p.count)), [series]);
  return (
    <div className="flex items-end gap-[3px] h-32 overflow-x-auto" data-testid="admin-signups-chart">
      {series.map((p) => {
        const h = Math.max(2, Math.round((p.count / max) * 100));
        return (
          <div
            key={p.date}
            className="flex-1 min-w-[6px] flex flex-col justify-end"
            title={`${p.date}: ${p.count}`}
          >
            <div className="bg-[#1F9FA4] rounded-t-sm" style={{ height: `${h}%` }} />
          </div>
        );
      })}
    </div>
  );
};
